// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, computed, inject, ChangeDetectionStrategy, signal, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { NgbAccordionModule, NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { take } from 'rxjs/operators';
import { SharedModule } from '@lfx/shared/shared.module';
import { LfBoxComponent } from '@lfx/shared/components/lf-box/lf-box.component';
import { NewTableComponent } from '@lfx/shared/components/new-table/new-table.component';
import { WidgetMenuComponent, WidgetAction, WidgetActionClickEvent } from '@lfx/shared/components/widget-menu/widget-menu.component';
import { InfluenceChartComponent } from '../our-influence/charts/influence-chart.component';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { MetricCardData } from '../our-influence/metrics-carousel.component';
import { KeyProjectsService } from '@lfx/key-projects/services/key-projects.service';
import { MetricChartConfigService, UserProfileData } from '@lfx/key-projects/services/metric-chart-config.service';
import { FlyoutService, ToastService } from '@lfx/core/services';
import { ModalService } from '@lfx/shared/services/modal.service';
import { TEMPLATE_IDENTIFIERS } from '@lfx/key-projects/key-projects.model';
import { ReportIssueModalComponent } from '../report-issue-modal/report-issue-modal.component';
import { ReportIssueEntity } from '@lfx/key-projects/entities/report-issue.entity';

export interface MetricChartOffcanvasData {
  projectName?: string;
  projectSlug?: string;
  chartData: MetricCardData;
}

@Component({
  selector: 'lfx-metric-chart-offcanvas',
  standalone: true,
  imports: [CommonModule, SharedModule, LfBoxComponent, NewTableComponent, InfluenceChartComponent,
    NgbAccordionModule, WidgetMenuComponent],
  templateUrl: './metric-chart-offcanvas.component.html',
  styleUrls: ['./metric-chart-offcanvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MetricChartOffcanvasComponent implements OnInit {
  // Template reference for user profile cell
  @ViewChild('userProfileTemplate', { static: true }) userProfileTemplate: TemplateRef<unknown>;

  // Writable signal for programmatic assignment from services
  data = signal<MetricChartOffcanvasData | undefined>(undefined);

  // Loading state
  private readonly _loading = signal<boolean>(true);

  // Track completion of individual loading operations
  private tableDataLoaded = false;
  private summaryTableLoaded = false;

  // Data signals
  private readonly _tableColumns = signal<Column[]>([]);
  private readonly _tableData = signal<DataRow[]>([]);
  private readonly _loadingError = signal<string | null>(null);

  // Summary table signals
  private readonly _summaryTableColumns = signal<Column[]>([]);
  private readonly _summaryTableData = signal<DataRow[]>([]);
  private readonly _summaryTableError = signal<string | null>(null);

  private readonly activeOffcanvas = inject(NgbActiveOffcanvas);
  private readonly keyProjectsService = inject(KeyProjectsService);
  private readonly metricChartConfigService = inject(MetricChartConfigService);
  private readonly flyoutService = inject(FlyoutService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);

  protected readonly widgetActions = signal<WidgetAction[]>([
    {
      id: 'report',
      tooltip: 'Report issue',
      icon: 'fal fa-comment-exclamation',
      ariaLabel: 'Report issue with this data'
    },
  ]);

  ngOnInit(): void {
    // Only load data if we don't have null values
    if (!this.hasNullValues()) {
      this.loadTableData();
      this.initializeSummaryTable();
    } else {
      // Skip loading and mark as complete for null values
      this.tableDataLoaded = true;
      this.summaryTableLoaded = true;
      this.checkLoadingComplete();
    }
  }

  /**
   * Check if all data has loaded and update loading state
   */
  private checkLoadingComplete(): void {
    if (this.tableDataLoaded && this.summaryTableLoaded) {
      this._loading.set(false);
    }
  }

  /**
   * Initialize summary table with data from service
   */
  private initializeSummaryTable(): void {
    const data = this.data();
    const metricType = data?.chartData?.metricType;
    const projectSlug = data?.projectSlug;

    this._summaryTableError.set(null);

    this.keyProjectsService.getSummaryTableData(metricType, projectSlug).subscribe({
      next: (response) => {
        this._summaryTableColumns.set(response.columns);
        this._summaryTableData.set(response.data);
        this._summaryTableError.set(null);
        this.summaryTableLoaded = true;
        this.checkLoadingComplete();
      },
      error: (error) => {
        console.error('Error loading summary table data:', error);
        this._summaryTableError.set('Failed to load summary data');
        this._summaryTableColumns.set([]);
        this._summaryTableData.set([]);
        this.summaryTableLoaded = true;
        this.checkLoadingComplete();
      }
    });
  }

  /**
   * Load table columns and data from the service
   */
  private loadTableData(): void {
    const data = this.data();
    const metricType = data?.chartData?.metricType;
    const projectSlug = data?.projectSlug;

    // Load columns first
    this.keyProjectsService.getMetricChartColumns(metricType).subscribe({
      next: (columns) => {
        this._tableColumns.set(columns);

        // Now load table data
        this.keyProjectsService.getMetricChartData(metricType, projectSlug).subscribe({
          next: (tableData) => {
            this._tableData.set(tableData);
            this._loadingError.set(null);
            this.tableDataLoaded = true;
            this.checkLoadingComplete();
          },
          error: (error) => {
            console.error('Error loading table data:', error);
            this._loadingError.set('Failed to load table data');
            this._tableData.set([]);
            this.tableDataLoaded = true;
            this.checkLoadingComplete();
          }
        });
      },
      error: (error) => {
        console.error('Error loading columns:', error);
        this._loadingError.set('Failed to load columns');
        this._tableColumns.set([]);
        this._tableData.set([]);
        this.tableDataLoaded = true;
        this.checkLoadingComplete();
      }
    });
  }

  // Computed signals for template data - protected since used by template
  protected readonly title = computed(() => {
    const data = this.data();
    const projectName = data?.projectName || 'Project';
    const chartTitle = data?.chartData?.title || 'Metrics';

    return `Our ${projectName} ${chartTitle}`;
  });

  protected readonly tableColumns = computed((): Column[] => {
    const columns = this._tableColumns();
    // Process columns to assign template references using type-safe constants
    return columns.map(column => {
      if (typeof column.template === 'string' && column.template === TEMPLATE_IDENTIFIERS.USER_PROFILE) {
        return { ...column, template: this.userProfileTemplate };
      }
      return column;
    });
  });

  protected readonly tableData = computed((): DataRow[] => {
    return this._tableData();
  });

  protected readonly chartData = computed(() => {
    const data = this.data();
    return data?.chartData;
  });

  protected readonly hasNullValues = computed(() => {
    const chartData = this.chartData();
    return chartData?.iconType === 'no-signal';
  });

  protected readonly nullValueDescription = computed(() => {
    const chartData = this.chartData();
    return chartData?.bottomSummary?.description;
  });

  protected readonly isTableLoading = computed(() => {
    return this._loading();
  });

  protected readonly loadingError = computed(() => {
    return this._loadingError();
  });

  // Summary table computed signals
  protected readonly summaryTableColumns = computed((): Column[] => {
    return this._summaryTableColumns();
  });

  protected readonly summaryTableData = computed((): DataRow[] => {
    return this._summaryTableData();
  });

  protected readonly isSummaryTableLoading = computed(() => {
    return this._loading();
  });

  protected readonly summaryTableError = computed(() => {
    return this._summaryTableError();
  });

  // Event handler for template - protected since used by template
  protected closeModal(): void {
    this.activeOffcanvas.close();
  }

  /**
   * Handle column text click events from the table
   * Delegates to service to determine if user profile should be opened
   */
  protected onColumnTextClick(event: { row: DataRow; column: Column }): void {
    const { row, column } = event;
    const data = this.data();
    const metricType = data?.chartData?.metricType || '';

    // Delegate to service to check if this column should open user profile
    if (!this.metricChartConfigService.shouldOpenUserProfile(metricType, column)) {
      return;
    }

    // Get the user profile field name from service
    const userProfileField = this.metricChartConfigService.getUserProfileField(metricType);
    if (!userProfileField) {
      console.warn(`No user profile field found for metric type: ${metricType}`);
      return;
    }

    // Extract user profile data using service and open profile
    const userProfileData = this.metricChartConfigService.extractUserProfileData(row, userProfileField);
    if (userProfileData) {
      this.openUserProfile(userProfileData);
    }
  }

  /**
   * Open user profile flyout for contributors, maintainers, committers, etc.
   * @param userProfileData - The extracted user profile data
   */
  private openUserProfile(userProfileData: UserProfileData): void {
    if (!userProfileData.memberId) {
      console.warn('No member ID found for user profile');
      return;
    }

    const data = this.data();

    this.flyoutService.openUserProfile({
      employeeId: userProfileData.memberId,
      employeeEmail: '', // We don't have email from this data
      defaultEmployeeObject: {
        name: userProfileData.memberDisplayName || 'Unknown Contributor',
        logoUrl: userProfileData.memberLogoUrl || '',
        email: '', // We don't have email from this data
      },
      options: {
        "activity": {
          collapsed: false,
          defaultFilter: "Technical"
        }
      },
      projectId: data?.projectSlug, // Using project slug as project ID
    });
  }

  /**
   * Handle widget action click events
   * Delegates to appropriate handler based on action ID
   */
  protected handleWidgetAction(event: WidgetActionClickEvent): void {
    if (event.actionId === 'report') {
      this.openReportIssueModal();
    }
  }

  /**
   * Open report issue modal for users to report data issues
   */
  private openReportIssueModal(): void {
    const data = this.data();
    const metricType = data?.chartData?.metricType || '';
    const metricDisplayName = ReportIssueEntity.getMetricDisplayName(metricType);
    const currentUrl = window.location.href;

    this.modalService
      .openComponent(ReportIssueModalComponent, {
        inputs: {
          metricType,
          metricDisplayName,
          projectSlug: data?.projectSlug,
          currentUrl,
        },
        options: {
          windowClass: 'report-issue-modal',
          backdrop: 'static',
          centered: true,
        }
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result?.success) {
          const config = ReportIssueEntity.getConfig();
          this.toastService.showSuccess(
            config.display.toastMessage,
            {
              header: config.display.toastHeader,
              link: result.webUrl ? { text: config.display.viewIssueLinkText, url: result.webUrl } : undefined,
              delay: 10000,
            }
          );
        }
      });
  }
}
